/**
 * 🧮 CALCULADORA MELD 3.0
 * Model for End-Stage Liver Disease
 * Fórmula oficial MDCalc 2023
 * https://www.mdcalc.com/calc/10437/model-end-stage-liver-disease-meld
 */

export interface DadosMELD {
  creatinina: number; // mg/dL
  bilirrubina: number; // mg/dL
  inr: number; // razão
  sodio: number; // mEq/L
  albumina: number; // g/dL
  sexoFeminino: boolean;
}

export interface ResultadoMELD {
  score: number;
  classificacao: "Baixo" | "Moderado" | "Alto" | "Crítico";
  cor: string;
  mortalidade90dias: string;
  conduta: string;
}

/**
 * Calcula o MELD 3.0 - Fórmula Oficial MDCalc
 *
 * Fórmula:
 * MELD 3.0 = 1.33 (se mulher) +
 *            4.56 × ln(bilirrubina) +
 *            0.82 × (137 - sódio) -
 *            0.24 × (137 - sódio) × ln(bilirrubina) +
 *            9.09 × ln(INR) +
 *            11.14 × ln(creatinina) +
 *            1.85 × (3.5 - albumina) -
 *            1.83 × (3.5 - albumina) × ln(creatinina) +
 *            6
 */
export function calcularMELD(dados: DadosMELD): ResultadoMELD {
  // Aplicar limites conforme fórmula oficial OPTN/UNOS.
  // Bilirrubina: piso 1.0 (sem teto — MELD 3.0 não limita bilirrubina).
  const bili = Math.max(1.0, dados.bilirrubina);

  // INR: piso 1.0 (sem teto).
  const inr = Math.max(1.0, dados.inr);

  // Creatinina: piso 1.0, teto 3.0.
  const cre = Math.max(1.0, Math.min(3.0, dados.creatinina));

  // Sódio: piso 125, teto 137.
  const na = Math.max(125, Math.min(137, dados.sodio));

  // Albumina: piso 1.5, teto 3.5.
  const alb = Math.max(1.5, Math.min(3.5, dados.albumina));

  // Logaritmos naturais
  const lnBili = Math.log(bili);
  const lnINR = Math.log(inr);
  const lnCre = Math.log(cre);

  // Cálculo termo a termo (MELD 3.0)
  const termo1 = dados.sexoFeminino ? 1.33 : 0;
  const termo2 = 4.56 * lnBili;
  const termo3 = 0.82 * (137 - na);
  const termo4 = -0.24 * (137 - na) * lnBili;
  const termo5 = 9.09 * lnINR;
  const termo6 = 11.14 * lnCre;
  const termo7 = 1.85 * (3.5 - alb);
  const termo8 = -1.83 * (3.5 - alb) * lnCre;
  const termo9 = 6;

  // Soma de todos os termos
  const meldRaw =
    termo1 +
    termo2 +
    termo3 +
    termo4 +
    termo5 +
    termo6 +
    termo7 +
    termo8 +
    termo9;

  // Arredondar e limitar entre 6 e 40
  const score = Math.max(6, Math.min(40, Math.round(meldRaw)));

  return classificarMELD(score);
}

/**
 * Classifica o score MELD
 */
function classificarMELD(score: number): ResultadoMELD {
  if (score < 10) {
    return {
      score,
      classificacao: "Baixo",
      cor: "#10b981",
      mortalidade90dias: "< 2%",
      conduta: "Acompanhamento ambulatorial",
    };
  }

  if (score >= 10 && score < 20) {
    return {
      score,
      classificacao: "Moderado",
      cor: "#f59e0b",
      mortalidade90dias: "6-20%",
      conduta: "Avaliar inclusão em lista",
    };
  }

  if (score >= 20 && score < 30) {
    return {
      score,
      classificacao: "Alto",
      cor: "#f97316",
      mortalidade90dias: "20-40%",
      conduta: "Prioridade em lista",
    };
  }

  return {
    score,
    classificacao: "Crítico",
    cor: "#ef4444",
    mortalidade90dias: "> 40%",
    conduta: "URGENTE - Status especial",
  };
}

/**
 * Valida se todos os valores estão presentes
 */
export function validarDadosMELD(dados: Partial<DadosMELD>): boolean {
  return !!(
    dados.creatinina &&
    dados.bilirrubina &&
    dados.inr &&
    dados.sodio &&
    dados.albumina
  );
}
